import * as _ from 'lodash-es';
import Block, { BlockEmbed } from 'quill/blots/block';
import Quill, { Delta, Op, Parchment } from 'quill/core';
import type { Bounds, Range } from 'quill/core';
import Link from 'quill/formats/link';
import type { ReactElement } from 'react';
import selectNode from '../utils/editor/selectNode';
import closestLine from './closestLine';
import { READONLY_CHANGE_EVENT } from './constants';
import Scroll from './formats/Scroll';
import getSelectedEmbed from './getSelectedEmbed';
import patchQuill from './patchQuill';
import deltaToText from './utils/deltaToText';
import isSelectableInlineEmbed from './utils/isSelectableInlineEmbed';
import { isClickingInsideCarouselButtons } from './image/CarouselControls';
import { getScrollContainer } from '../post/listenToPostScroll';
import getReferenceNodeForBounds from './utils/getReferenceNodeForBounds';
import selectParagraphsWithTripleClick from './selectParagraphsWithTripleClick';
import { getDefaultAttachmentMentionText } from './formats/AttachmentMention';
import type { AttachmentFile } from './formats/Attachment';
import getRangeFromPoint from './utils/getRangeFromPoint';

export type RenderPortalArgs = {
  readOnly: boolean;
  selected: boolean;
};

export type PortalBlot = Parchment.Blot & {
  id: string;
  renderPortal: (args: RenderPortalArgs) => ReactElement;
};

export type ModuleConfig = Record<
  string,
  boolean | null | Record<string, unknown>
>;

export interface QuillOptions {
  content: Delta | Op[];
  formats: Parchment.RegistryDefinition[];
  readOnly: boolean;
  theme?: unknown;
  embedSelectionContainer: HTMLElement | null;
  onBlotMounted: (...blots: PortalBlot[]) => void;
  onBlotUnmounted: (unmountedBlot: PortalBlot) => void;
  onSelectionChange: (range: Range | null, yChanged?: boolean) => void;
  onTextChange: (delta: Delta, oldContent: Delta, source: string) => void;
  onReadOnlyChange: (readOnly: boolean) => void;
  onActiveBlotChange: (blot: Parchment.Blot | null) => void;
  noTitleLine?: boolean;
  rootClassName?: string;
  moduleConfig?: ModuleConfig;
  onUploadError?: (message: string) => void;
}

class QuillEditor extends Quill {
  noTitleLine: boolean;
  contentBlank: boolean | null = null;
  declare options: (typeof Quill)['prototype']['options'] & {
    onReadOnlyChange: (readOnly: boolean) => void;
    onActiveBlotChange: (blot: Parchment.Blot | null) => void;
  };

  constructor(container: HTMLDivElement, args: QuillOptions) {
    const {
      content,
      formats,
      readOnly,
      moduleConfig,
      theme,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      noTitleLine,
      embedSelectionContainer,
      rootClassName,
      onSelectionChange,
      onTextChange,
      onBlotMounted,
      onBlotUnmounted,
      onReadOnlyChange,
      onUploadError,
      onActiveBlotChange,
    } = args;

    const registry = new Parchment.Registry();
    formats.forEach((format) => registry.register(format));
    const modules: Record<string, unknown> = {
      clipboard: true,
      history: { userOnly: true },
      uploader: {
        onUploadError,
      },
      rangeSaver: true,
      ...(moduleConfig || {}),
    };

    // When the value of a module is `null` or `undefined`, Quill still tries to import
    // it although it won't be enabled.
    // To support disabling modules via `moduleConfig`, we remove `null` and `undefined` values
    // here to prevent the disabled modules from being imported at all.
    Object.keys(modules).forEach((name) => {
      if (modules[name] == null) {
        delete modules[name];
      }
    });

    const config = {
      readOnly,
      registry,
      theme,
      modules,
      onReadOnlyChange,
      onActiveBlotChange,
    };
    // @ts-expect-error
    super(container, config);
    this.noTitleLine = noTitleLine || false;
    // @ts-expect-error
    this.scroll.noTitleLine = this.noTitleLine;
    this.root.classList.add('editor');
    if (rootClassName) {
      rootClassName.split(/\s/).forEach((className) => {
        this.root.classList.add(className);
      });
    }
    this.on(Quill.events.SELECTION_CHANGE, (newRange) => {
      if (newRange) {
        onSelectionChange(newRange, true);
      }
    });
    this.on(Quill.events.TEXT_CHANGE, (delta, oldContent, source) => {
      onTextChange(delta, oldContent, source);

      if (!this.noTitleLine && source === Quill.sources.USER) {
        const canCauseMentionToAppearOnTitle = delta.ops.some(
          (op) =>
            op.delete ||
            (typeof op.insert === 'object' &&
              (op.insert.mention || op.insert['attachment-mention'])),
        );
        if (canCauseMentionToAppearOnTitle) {
          _.defer(() => {
            this.convertMentionOnTitleLine();
          });
        }
      }
    });
    this.root.addEventListener('keyup', (e) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        onSelectionChange(this.getSelection(), false);
      } else if (e.key === 'Enter') {
        onSelectionChange(this.getSelection(), true);
      }
    });
    this.root.addEventListener('beforeinput', (e) => {
      if (e.defaultPrevented) return;
      if (e.inputType === 'insertText' && !e.isComposing) {
        const [staticRange] = e.getTargetRanges();
        if (staticRange?.collapsed === false && e.data) {
          const normalized = this.selection.normalizeNative(staticRange);
          if (normalized) {
            const range = this.selection.normalizedToRange(normalized);
            this.keyboard.handleDeleteRange(range);
            this.insertText(range.index, e.data, Quill.sources.USER);
            this.setSelection(
              range.index + e.data.length,
              0,
              Quill.sources.SILENT,
            );
            e.preventDefault();
          }
        }
      }
    });
    this.root.addEventListener('mousedown', (e) => {
      if (e.detail >= 3 && e.button === 0) {
        const blot =
          e.target instanceof Element && this.scroll.find(e.target, true);

        if (blot && !(blot instanceof Parchment.EmbedBlot)) {
          const isEventHandled = selectParagraphsWithTripleClick(this);
          if (isEventHandled) {
            e.preventDefault();
          }
        }
      } else {
        this.handleAnchorMousedown(e);
      }
    });
    this.emitter.on(Quill.events.EDITOR_CHANGE, (type: string) => {
      if (type === Quill.events.TEXT_CHANGE) {
        this.updateBlankClasses();
      } else if (
        type === Quill.events.SELECTION_CHANGE &&
        this.selection.lastNative
      ) {
        this.handleSelectionChange(this.selection.lastNative);
      }
    });
    this.scroll.emitter.on(Scroll.events.BLOT_MOUNT, onBlotMounted);
    this.scroll.emitter.on(Scroll.events.BLOT_UNMOUNT, onBlotUnmounted);
    patchQuill(this, embedSelectionContainer);
    this.updateBlankClasses();
    this.setContents(content);
    this.history.clear();
    this.listenEmbedSelection();
  }

  get titleLine() {
    if (this.noTitleLine) return null;
    const { head } = this.scroll.children;
    return head instanceof Block ? head : null;
  }

  private handleSelectionChange({
    start,
    end,
  }: {
    start: { node: Node };
    end: { node: Node };
  }) {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    const collapsed = start.node === end.node;

    const startBlot = this.scroll.find(start.node);
    if (collapsed && isSelectableInlineEmbed(startBlot)) {
      this.changeActiveBlot(startBlot);
      return;
    }

    const startLine = closestLine(startBlot);
    if (collapsed && startLine instanceof BlockEmbed) {
      this.changeActiveBlot(startLine);
    } else if (
      startLine &&
      startLine.statics.blotName === 'table' &&
      this.isEnabled()
    ) {
      // @ts-expect-error checked with `blotName === 'table'`
      const table = startLine.table();
      if (table) {
        this.changeActiveBlot(table);
      }
    } else {
      this.changeActiveBlot(null);
    }
  }

  changeActiveBlot(blot: Parchment.Blot | null) {
    this.options.onActiveBlotChange(blot);
    const node = blot?.domNode ?? null;
    selectNode(node instanceof Element ? node : null, this.root);
  }

  private updateBlankClasses() {
    this.root.classList.toggle('title-blank', this.isTitleBlank());
    this.root.classList.toggle('content-blank', this.isContentBlank());
  }

  private isTitleBlank() {
    if (this.noTitleLine) return false;
    const first = this.scroll.children.head;
    return first == null || first.length() <= 1;
  }

  isTitleFocused() {
    if (!this.titleLine) return false;
    const { lastRange } = this.selection;
    if (lastRange == null) return false;
    const [line] = this.getLine(lastRange.index);
    return line === this.titleLine;
  }

  private isContentBlank() {
    const titleLineLength = this.noTitleLine ? 0 : 1;
    if (this.scroll.children.length <= titleLineLength) return true;
    if (this.scroll.children.length > titleLineLength + 1) return false;
    const lines = this.getLines();
    if (lines.length > titleLineLength + 1) return false;
    const second = lines[titleLineLength];
    return (
      !second ||
      (second instanceof Block &&
        second.length() <= 1 &&
        Object.keys(second.formats() || {}).length === 0)
    );
  }

  private listenEmbedSelection() {
    this.root.addEventListener('click', (e) => {
      const blot =
        e.target instanceof Node ? this.scroll.find(e.target, true) : null;
      if (!blot) return;
      if (blot instanceof BlockEmbed || isSelectableInlineEmbed(blot)) {
        const range = this.getSelection();

        // In newer WebKit versions, the textarea element inside a blot (e.g. ImageBlot)
        // will not have a selection anymore when clicked, which would cause
        // the selection to be reset and the textarea to loose focus.
        // The textarea behaviour seems to be intended from WebKit,
        // see https://bugs.webkit.org/show_bug.cgi?id=252281.
        if (
          !range &&
          e.target instanceof HTMLTextAreaElement &&
          blot.domNode.contains(e.target)
        ) {
          return;
        }

        // When navigating the carousel with the controls, we don't want to set the selection
        if (isClickingInsideCarouselButtons(e)) {
          return;
        }

        // If the editor is not focused, other event handlers should
        // have already moved the focus to other elements programmatically.
        // E.g. to comment input.
        if (this.hasFocus()) {
          const index = blot.offset(this.scroll);
          if (!range || range.index !== index || range.length !== 1) {
            this.setSelection(index, 1, Quill.sources.USER);
          }
        }
      }
    });
  }

  lineFromYOffset(yOffset: number) {
    const editorComputedStyle = getComputedStyle(this.root);
    const editorLeftPadding = parseInt(editorComputedStyle.paddingLeft, 10);
    const editorRect = this.root.getBoundingClientRect();
    const editorCenterOffset =
      editorRect.left + editorLeftPadding + editorRect.width / 2;

    const target = document
      .elementsFromPoint(editorCenterOffset, yOffset)
      .find((element) => this.scroll.find(element, true));
    const blot = target && this.scroll.find(target, true);
    return blot ? closestLine(blot) : null;
  }

  enable(enabled: boolean) {
    super.enable(enabled);
    const { onReadOnlyChange } = this.options;
    if (onReadOnlyChange) {
      onReadOnlyChange(!enabled);
    }
    this.emitter.emit(READONLY_CHANGE_EVENT, !enabled);
  }

  focusWithoutScrolling() {
    this.root.focus({ preventScroll: true });
  }

  getClientRects(index: number, length: number) {
    let rects: Bounds[] = [];

    const [startLeaf, startLeafOffset] = this.scroll.leaf(index);
    if (startLeaf instanceof BlockEmbed && length <= 1) {
      rects = [...getReferenceNodeForBounds(startLeaf).getClientRects()];
    } else if (
      isSelectableInlineEmbed(startLeaf) &&
      startLeafOffset === 0 &&
      length === 1
    ) {
      rects = [...getReferenceNodeForBounds(startLeaf).getClientRects()];
    } else {
      const [endLeaf, endLeafOffset] = this.scroll.leaf(index + length);
      if (startLeaf == null || endLeaf == null) return [];

      if (
        isSelectableInlineEmbed(endLeaf) &&
        endLeafOffset === 1 &&
        length === 1
      ) {
        rects = [...getReferenceNodeForBounds(endLeaf).getClientRects()];
      } else {
        const range = document.createRange();
        const [startNode, startNodeOffset] = startLeaf.position(
          startLeafOffset,
          true,
        );
        range.setStart(startNode, startNodeOffset);
        const [endNode, endNodeOffset] = endLeaf.position(endLeafOffset, true);
        if (startNode === endNode) {
          range.setEnd(endNode, endNodeOffset);
          rects = Array.from(range.getClientRects());
        } else {
          range.setEndAfter(startNode.childNodes[startNodeOffset] || startNode);
          rects = Array.from(range.getClientRects());
          range.setStartBefore(endNode.childNodes[endNodeOffset] || endNode);
          range.setEnd(endNode, endNodeOffset);
          rects = rects.concat(Array.from(range.getClientRects()));
        }
        this.scroll.descendants(
          (child: Parchment.Blot) => {
            if (child === startLeaf || child === endLeaf) return false;
            if (child.statics.blotName === 'text') {
              range.selectNode(child.domNode);
              rects = rects.concat(Array.from(range.getClientRects()));
              return true;
            }
            return false;
          },
          index,
          length,
        );
      }
    }
    const containerBounds = this.container.getBoundingClientRect();
    return rects
      .map((bounds) => {
        return {
          left: bounds.left - containerBounds.left,
          right: bounds.right - containerBounds.left,
          width: bounds.width,
          top: bounds.top - containerBounds.top,
          bottom: bounds.bottom - containerBounds.top,
          height: bounds.height,
        };
      })
      .filter((bounds) => {
        return bounds.height > 1 && bounds.width > 1;
      });
  }

  getBounds(index: number | { index: number; length: number }, length = 0) {
    if (length === 0 || (typeof index === 'object' && index.length === 0)) {
      return super.getBounds(index, length);
    }
    if (typeof index !== 'number') {
      length = index.length; // eslint-disable-line no-param-reassign
      index = index.index; // eslint-disable-line no-param-reassign
    }
    const rects = this.getClientRects(index, length);
    const bounds = _.reduce(
      rects,
      (memo, rect) => ({
        ...memo,
        bottom: Math.max(rect.bottom, memo.bottom),
        left: Math.min(rect.left, memo.left),
        right: Math.max(rect.right, memo.right),
        top: Math.min(rect.top, memo.top),
      }),
      {
        bottom: Number.MIN_SAFE_INTEGER,
        left: Number.MAX_SAFE_INTEGER,
        right: Number.MIN_SAFE_INTEGER,
        top: Number.MAX_SAFE_INTEGER,
      },
    );
    return {
      ...bounds,
      height: bounds.bottom - bounds.top,
      width: bounds.right - bounds.left,
    };
  }

  getText(range: { index: number; length: number }): string;
  getText(index: number, length: number): string;
  getText(index: number | { index: number; length: number }, length?: number) {
    // @ts-expect-error getContents actually supports the overloading
    const delta = this.getContents(index, length);
    return deltaToText(delta);
  }

  getTitle() {
    if (this.noTitleLine) return '';
    const [line] = this.getLine(0);
    return line ? deltaToText(line.delta()).replace('\n', '') : '';
  }

  activateSelection() {
    if (!this.isEnabled()) return;

    const range = this.getSelection();
    if (range) {
      // Make sure the existing selection is focused so it can receive user inputs.
      if (!this.hasFocus()) {
        const selectedEmbed = getSelectedEmbed(this, range);
        // @ts-expect-error
        if (typeof selectedEmbed?.activateSelection === 'function') {
          // @ts-expect-error
          selectedEmbed.activateSelection();
        } else {
          this.focusWithoutScrolling();
          if (selectedEmbed) {
            // Restore the previous selection reset by `this.root.focus()`.
            // This is necessary for selectable embeds because their selections
            // are outside of the editor.
            this.setSelection(range.index, range.length, Quill.sources.SILENT);
          }
        }
      }
      return;
    }

    // There's no selection in the editor.
    // Move the cursor to:
    // - The end of the title line if `noTitleLine` is `false`;
    // - The beginning of the first line otherwise.
    let targetIndex = 0;
    const [firstLine] = this.getLine(0);
    if (!firstLine) return;
    if (!this.noTitleLine) {
      targetIndex = firstLine.offset(this.scroll) + firstLine.length() - 1;
    }
    this.focusWithoutScrolling(); // focus first to prevent scrolling caused by setSelection
    this.setSelection(targetIndex, 0, Quill.sources.SILENT);
  }

  scrollSelectionIntoView() {
    const range = this.selection.lastRange;
    const [leaf] = range?.length === 1 ? this.getLeaf(range.index) : [null];

    if (!(leaf instanceof BlockEmbed)) {
      super.scrollSelectionIntoView();
      return;
    }

    if (['table-embed', 'code-embed'].includes(leaf.statics.blotName)) {
      return;
    }
    const bounds = leaf.domNode.getBoundingClientRect();
    const scrollingContainer = getScrollContainer();
    const scrollBounds = scrollingContainer?.getBoundingClientRect();

    const hasFloatingToolbar = [
      'image',
      'attachment',
      'iframe',
      'embed',
      'video',
    ].includes(leaf.statics.blotName);
    const spaceForToolbar = 54;
    const top = bounds.top - (hasFloatingToolbar ? spaceForToolbar : 0);
    const height = bounds.bottom - top;

    const isScrollingAllowed = scrollBounds && height <= scrollBounds.height;
    if (isScrollingAllowed) {
      this.scrollRectIntoView({
        top,
        right: bounds.right,
        bottom: bounds.bottom,
        left: bounds.left,
      });
    }
  }

  private convertMentionOnTitleLine() {
    if (!this.titleLine) return;
    const delta = this.titleLine.delta();
    const normalization = new Delta();
    delta.ops.forEach((op) => {
      if (typeof op.insert === 'object' && op.insert.mention) {
        normalization.delete(1);
        const attributes = op.attributes || {};
        const mentionContent = attributes['mention-text'] || attributes.content;
        if (typeof mentionContent === 'string') {
          normalization.insert(mentionContent);
        }
      } else if (
        typeof op.insert === 'object' &&
        op.insert['attachment-mention'] &&
        Array.isArray(op.insert['attachment-mention'])
      ) {
        normalization.delete(1);
        const attributes = op.attributes || {};
        const mentionContent =
          attributes['mention-text'] ||
          getDefaultAttachmentMentionText(
            op.insert['attachment-mention'] as AttachmentFile[],
          );

        if (typeof mentionContent === 'string') {
          normalization.insert(mentionContent);
        }
      } else if (op.retain || op.insert) {
        normalization.retain(Op.length(op));
      }
    });
    if (normalization.ops.some((op) => op.delete)) {
      this.updateContents(normalization, Quill.sources.USER);
    }
  }

  private handleAnchorMousedown(e: MouseEvent) {
    if (!e.shiftKey || e.defaultPrevented) return;
    const blot =
      e.target instanceof HTMLAnchorElement ? this.scroll.find(e.target) : null;

    if (!(blot instanceof Link)) return;
    if (!this.isEnabled()) return;

    const oldRange = this.getSelection();
    if (oldRange?.length !== 0) return;

    const nativeRange = getRangeFromPoint(e.clientX, e.clientY);
    const normalized =
      nativeRange && this.selection.normalizeNative(nativeRange);
    if (!normalized) return;

    const range = this.selection.normalizedToRange(normalized);
    this.setSelection(
      Math.min(range.index, oldRange.index),
      Math.abs(range.index - oldRange.index),
      Quill.sources.USER,
    );

    e.preventDefault();
  }
}

export default QuillEditor;
